// Badge colors, keyed on the normalized (lowercased) label so spacing/casing variants
// collapse to one color, and grouped by sentiment: greens for wins, reds for hard "no",
// amber for "not now", slate for machine/no-contact outcomes. Module-level so it isn't
// rebuilt once per rendered row.
const DISPOSITION_STYLES: Record<string, string> = {
  // Positive / progressing outcomes.
  interested: 'bg-emerald-100 text-emerald-800',
  'meeting booked': 'bg-emerald-100 text-emerald-800',
  'call back': 'bg-teal-100 text-teal-800',
  callback: 'bg-teal-100 text-teal-800',
  // Soft-no / retry-later outcomes.
  'no answer': 'bg-amber-100 text-amber-800',
  'wrong number': 'bg-amber-100 text-amber-800',
  gatekeeper: 'bg-amber-100 text-amber-800',
  // Hard-no outcomes.
  'not interested': 'bg-red-100 text-red-800',
  'do not call': 'bg-red-100 text-red-800',
  // Machine / non-contact outcomes.
  voicemail: 'bg-slate-100 text-slate-600',
  abandoned: 'bg-slate-100 text-slate-600',
  failed: 'bg-slate-100 text-slate-600',
};

// The stored disposition is a JSON blob from the classifier ({disposition, label,
// reasoning, confidence}). Show the human `label` (falling back to a prettified id) with
// the reasoning in the hover tooltip. Falls back to the raw string if it isn't JSON.
export function formatDisposition(raw: string | null): {
  label: string;
  detail: string | undefined;
} {
  if (!raw) return { label: '—', detail: undefined };
  try {
    const d = JSON.parse(raw) as { disposition?: unknown; label?: unknown; reasoning?: unknown };
    const label =
      typeof d.label === 'string'
        ? d.label
        : typeof d.disposition === 'string'
          ? d.disposition.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
          : null;
    if (label) {
      return { label, detail: typeof d.reasoning === 'string' ? d.reasoning : undefined };
    }
  } catch {
    // Not JSON (e.g. a legacy free-text value) — show it verbatim.
  }
  return { label: raw, detail: raw };
}

// Anything unmapped falls through to a neutral slate rather than going unstyled.
export function dispositionStyle(label: string): string {
  return DISPOSITION_STYLES[label.trim().toLowerCase()] ?? 'bg-slate-100 text-slate-700';
}

// A small dot + word, so a human-answered call reads at a glance (green), a machine
// (voicemail) stands apart (rose), and "none" — never connected — stays quiet grey.
// Unknown values fall through to grey.
export function AnsweredByBadge({ answeredBy }: { answeredBy: string }) {
  const key = answeredBy.trim().toLowerCase();
  const dot =
    key === 'human' ? 'bg-emerald-500' : key === 'machine' ? 'bg-rose-400' : 'bg-slate-300';
  return (
    <span className="text-muted-foreground inline-flex items-center gap-1.5">
      <span className={`${dot} h-1.5 w-1.5 flex-none rounded-full`} aria-hidden />
      <span className="truncate">{answeredBy || '—'}</span>
    </span>
  );
}
